import { useState } from 'react';
import { Button, CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { RiAddLine, RiArrowDownSLine, RiCloseLine, RiDeleteBinLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { BuzonNucStatusEnum } from '@/shared/types/interfaces/BuzonJudicial.interface';
import type { BuzonNucPreference } from '@/shared/types/interfaces/BuzonJudicial.interface';
import { useUserPermissions } from '@/shared/hooks/useUserPermissions';

const ROW_ACTION_BUTTON_WIDTH = 126;

interface BuzonAccionesCellProps {
  row: BuzonNucPreference;
  isRowLoading: boolean;
  isDisabled: boolean;
  /** Cuando se especifica, reemplaza el tooltip de acción de TODOS los botones de esta celda — usado
   *  para explicar por qué están deshabilitados (ej. hay 2+ NUCs seleccionados por checkbox). */
  tooltipOverride?: string;
  onDeactivate: (row: BuzonNucPreference) => void;
  onCancelSolicitud: (row: BuzonNucPreference) => void;
  onActivate: (row: BuzonNucPreference) => void;
  onReactivate: (row: BuzonNucPreference) => void;
  onActivateNuevaInstancia: (row: BuzonNucPreference) => void;
}

/**
 * Celda "Acciones" extraída de BuzonJudicial.tsx. Para el punto 12 (caso HABILITADO por instancia,
 * `tipoHabilitacion === 2`), en vez de mostrar 2 botones lado a lado (deformaba la tabla por espacio),
 * se muestra un único botón "Desplegar acciones" que abre un menú con Habilitar (punto 11, agregar otra
 * instancia) y Deshabilitar. El resto de los estados conserva su botón único de siempre.
 *
 * Con 2+ NUCs seleccionados por checkbox, `isDisabled` llega en `true` y `tooltipOverride` explica que
 * debe usarse la acción masiva o deseleccionar — las acciones individuales de cada botón no cambian.
 *
 * Si el usuario es Entidad y su rol no le permite una acción, el botón (o la opción del menú) se muestra
 * deshabilitado con un tooltip que explica el motivo, en vez de ocultarse. Ese tooltip tiene prioridad
 * sobre `tooltipOverride`, porque la falta de permiso no se resuelve deseleccionando.
 */
const BuzonAccionesCell = ({
  row,
  isRowLoading,
  isDisabled,
  tooltipOverride,
  onDeactivate,
  onCancelSolicitud,
  onActivate,
  onReactivate,
  onActivateNuevaInstancia,
}: BuzonAccionesCellProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { isAdministrador, isEntidad, isMinisterioPublico } = useUserPermissions();
  
  const closeMenu = () => setAnchorEl(null);

  // Si NO es entidad (persona física), tiene permisos por defecto.
  // Si ES entidad, depende de los roles (Admin o Ministerio Público).
  const puedeHabilitar = !isEntidad || isAdministrador || isMinisterioPublico;
  
  // Si NO es entidad(persona física), puede deshabilitar.
  // Si ES entidad, solo si es Administrador.
  const puedeDeshabilitar = !isEntidad || isAdministrador;

  // Si el usuario es Entidad y no tiene permisos de acción mínimos, todas las acciones quedan
  // deshabilitadas (incluida Cancelar), con un tooltip que explica el motivo.
  const sinPermisos = isEntidad && !puedeHabilitar && !puedeDeshabilitar;

  const sinPermisoHabilitarTooltip = t(
    'buzonJudicial.tooltips.noPermissionActivate',
    'Su rol en la entidad no le permite habilitar NUCs en el buzón judicial. Solo los roles Administrador o Ministerio Público pueden hacerlo.',
  );
  const sinPermisoDeshabilitarTooltip = t(
    'buzonJudicial.tooltips.noPermissionDeactivate',
    'Su rol en la entidad no le permite deshabilitar NUCs en el buzón judicial. Solo el rol Administrador puede hacerlo.',
  );
  const sinPermisosTooltip = t(
    'buzonJudicial.tooltips.noPermissionManage',
    'Su rol en la entidad no tiene permisos para gestionar las solicitudes del buzón judicial.',
  );

  /** Tooltip de un botón: el motivo de falta de permiso manda sobre el override y el texto de la acción. */
  const tooltipAccion = (permitido: boolean, sinPermisoTooltip: string, accionTooltip: string) =>
    !permitido ? sinPermisoTooltip : tooltipOverride ?? accionTooltip;

  /** MUI pone `pointer-events: none` en los MenuItem deshabilitados; se reactiva para que el tooltip
   *  responda al hover, sin el resaltado de fondo. El click no hace nada porque no se pasa `onClick`. */
  const menuItemDeshabilitadoSx = {
    '&.Mui-disabled': { pointerEvents: 'auto' },
    '&.Mui-disabled:hover': { backgroundColor: 'transparent' },
  } as const;

  const mostrarMenuAcciones = row.estado === BuzonNucStatusEnum.HABILITADO && row.tipoHabilitacion === 2;

  return (
    <div className="flex justify-center w-full gap-1">
      {row.estado === BuzonNucStatusEnum.HABILITADO && (
        mostrarMenuAcciones ? (
          <>
            <Tooltip title={tooltipAccion(!sinPermisos, sinPermisosTooltip, t('buzonJudicial.tooltips.rowActionsMenu', 'Ver acciones disponibles para este NUC'))} arrow placement="left">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isDisabled || sinPermisos}
                  startIcon={isRowLoading ? <CircularProgress size={16} color="inherit" /> : <RiArrowDownSLine size={16} />}
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                  sx={{
                    minWidth: ROW_ACTION_BUTTON_WIDTH,
                    justifyContent: 'center',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('buzonJudicial.actions.showActions', 'Acciones')}
                </Button>
              </span>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
              <Tooltip title={puedeHabilitar ? '' : sinPermisoHabilitarTooltip} arrow placement="left">
                <MenuItem
                  disabled={!puedeHabilitar}
                  onClick={puedeHabilitar ? () => { closeMenu(); onActivateNuevaInstancia(row); } : undefined}
                  sx={menuItemDeshabilitadoSx}
                >
                  <ListItemIcon><RiAddLine size={16} /></ListItemIcon>
                  <ListItemText primary={t('buzonJudicial.actions.activate', 'Habilitar')} />
                </MenuItem>
              </Tooltip>
              <Tooltip title={puedeDeshabilitar ? '' : sinPermisoDeshabilitarTooltip} arrow placement="left">
                <MenuItem
                  disabled={!puedeDeshabilitar}
                  onClick={puedeDeshabilitar ? () => { closeMenu(); onDeactivate(row); } : undefined}
                  sx={menuItemDeshabilitadoSx}
                >
                  <ListItemIcon><RiDeleteBinLine size={16} /></ListItemIcon>
                  <ListItemText primary={t('buzonJudicial.actions.deactivate', 'Deshabilitar')} />
                </MenuItem>
              </Tooltip>
            </Menu>
          </>
        ) : (
          <Tooltip title={tooltipAccion(puedeDeshabilitar, sinPermisoDeshabilitarTooltip, t('buzonJudicial.tooltips.deactivate', 'Solicitar la deshabilitación de este NUC en el buzón judicial'))} arrow placement="left">
            <span>
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={isDisabled || !puedeDeshabilitar}
                startIcon={<RiDeleteBinLine size={16} />}
                onClick={() => onDeactivate(row)}
                sx={{
                  width: ROW_ACTION_BUTTON_WIDTH,
                  minWidth: ROW_ACTION_BUTTON_WIDTH,
                  maxWidth: ROW_ACTION_BUTTON_WIDTH,
                  justifyContent: 'center',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('buzonJudicial.actions.deactivate', 'Deshabilitar')}
              </Button>
            </span>
          </Tooltip>
        )
      )}

      {[BuzonNucStatusEnum.PENDIENTE_FIRMA, BuzonNucStatusEnum.PENDIENTE_REHABILITAR, BuzonNucStatusEnum.PENDIENTE_DESHABILITAR].includes(row.estado) && (
        <Tooltip title={tooltipAccion(!sinPermisos, sinPermisosTooltip, t('buzonJudicial.tooltips.cancel', 'Cancelar la solicitud pendiente para este NUC'))} arrow placement="left">
          <span>
            <Button
              size="small"
              color="warning"
              variant="outlined"
              disabled={isDisabled || sinPermisos}
              startIcon={isRowLoading ? <CircularProgress size={16} color="inherit" /> : <RiCloseLine size={16} />}
              onClick={() => onCancelSolicitud(row)}
              sx={{
                width: ROW_ACTION_BUTTON_WIDTH,
                minWidth: ROW_ACTION_BUTTON_WIDTH,
                maxWidth: ROW_ACTION_BUTTON_WIDTH,
                justifyContent: 'center',
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t('buzonJudicial.actions.cancel', 'Cancelar')}
            </Button>
          </span>
        </Tooltip>
      )}

      {row.estado === BuzonNucStatusEnum.NO_HABILITADO && (
        <Tooltip title={tooltipAccion(puedeHabilitar, sinPermisoHabilitarTooltip, t('buzonJudicial.tooltips.activate', 'Solicitar la habilitación de este NUC en el buzón judicial'))} arrow placement="left">
          <span>
            <Button
              size="small"
              variant="contained"
              disabled={isDisabled || !puedeHabilitar}
              startIcon={isRowLoading ? <CircularProgress size={16} color="inherit" /> : <RiAddLine size={16} />}
              onClick={() => onActivate(row)}
              sx={{
                width: ROW_ACTION_BUTTON_WIDTH,
                minWidth: ROW_ACTION_BUTTON_WIDTH,
                maxWidth: ROW_ACTION_BUTTON_WIDTH,
                justifyContent: 'center',
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t('buzonJudicial.actions.activate', 'Habilitar')}
            </Button>
          </span>
        </Tooltip>
      )}

      {row.estado === BuzonNucStatusEnum.DESHABILITADO && (
        <Tooltip title={tooltipAccion(puedeHabilitar, sinPermisoHabilitarTooltip, t('buzonJudicial.tooltips.reactivate', 'Solicitar la re-habilitación de este NUC. Requiere justificación.'))} arrow placement="left">
          <span>
            <Button
              size="small"
              variant="contained"
              disabled={isDisabled || !puedeHabilitar}
              startIcon={isRowLoading ? <CircularProgress size={16} color="inherit" /> : <RiAddLine size={16} />}
              onClick={() => onReactivate(row)}
              sx={{
                width: ROW_ACTION_BUTTON_WIDTH,
                minWidth: ROW_ACTION_BUTTON_WIDTH,
                maxWidth: ROW_ACTION_BUTTON_WIDTH,
                justifyContent: 'center',
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t('buzonJudicial.actions.activate', 'Habilitar')}
            </Button>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default BuzonAccionesCell;
